// KvkSearchInput — zoek op bedrijfsnaam in KvK handelsregister.
//
// Backend proxy: GET /api/kvk/search?q=... en /api/kvk/basisprofiel/:nr.
// Bij selectie: autofill KvK-nummer (A1.2) en factuuradres (A1.3)
// via `onFillSiblings`-callback. Rob kan ook vrije tekst invullen
// (natuurlijk persoon of buitenlandse klant).

import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { api } from '../api/client.js';

function debounce(fn, ms) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), ms);
    };
}

export default function KvkSearchInput({ value, onChange, disabled, onFillSiblings }) {
    const [inputValue, setInputValue] = useState(value || '');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (typeof value === 'string' && value !== inputValue) {
            setInputValue(value);
        }
    }, [value]);

    const search = useMemo(() => debounce(async (q) => {
        if (!q || q.trim().length < 2) {
            setOptions([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/api/kvk/search?q=${encodeURIComponent(q.trim())}`);
            setOptions(res?.results || []);
        } catch (err) {
            setError(err?.message || 'KvK niet bereikbaar');
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, 350), []);

    const handleSelect = async (_, picked) => {
        if (typeof picked === 'string') {
            // Vrije tekst: alleen de naam zetten, geen autofill.
            onChange(picked);
            return;
        }
        if (!picked) {
            onChange('');
            return;
        }
        // Picked = object uit KvK-zoek. Zet naam, fetch basisprofiel voor adres.
        onChange(picked.handelsnaam);
        try {
            const profile = await api.get(`/api/kvk/basisprofiel/${picked.kvkNummer}`);
            const addr = profile?.bezoek_adres?.oneliner
                || profile?.correspondentie_adres?.oneliner
                || '';
            onFillSiblings?.({
                'A1.2': picked.kvkNummer,
                ...(addr ? { 'A1.3': addr } : {}),
            });
        } catch {
            // Basisprofiel-faal is niet-fataal; naam + KvK-nummer blijven staan.
            onFillSiblings?.({ 'A1.2': picked.kvkNummer });
        }
    };

    return (
        <Autocomplete
            freeSolo
            disabled={disabled}
            value={value || null}
            inputValue={inputValue}
            onInputChange={(_, v, reason) => {
                setInputValue(v);
                if (reason === 'input') {
                    onChange(v);
                    search(v);
                }
            }}
            onChange={handleSelect}
            options={options}
            loading={loading}
            filterOptions={(x) => x} // backend doet filter
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt?.handelsnaam || ''))}
            isOptionEqualToValue={(opt, v) => {
                if (typeof opt === 'string' || typeof v === 'string') return opt === v;
                return opt?.kvkNummer === v?.kvkNummer;
            }}
            renderOption={(props, opt) => {
                const { key, ...rest } = props;
                return (
                    <Box component="li" key={key || opt.kvkNummer} {...rest}>
                        <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap>{opt.handelsnaam}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                KvK {opt.kvkNummer} · {opt.type}
                                {opt.vestigingsnummer ? ` · vestiging ${opt.vestigingsnummer}` : ''}
                            </Typography>
                        </Box>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Zoek bedrijfsnaam of typ vrije tekst"
                    error={Boolean(error)}
                    helperText={error || undefined}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
